import React, { useEffect, useMemo, useRef, useState } from "react";
import { Collection, fetchCollections, getCurrentUser } from "../../api";
import { useAuth0 } from "@auth0/auth0-react";
import { tourStepsTimedTrials } from "./tourStepsTimedTrials";
import GuidedTour from "../../components/GuidedTour";
import { useNavigate } from "react-router-dom";
import BubbleText from "../../components/BubbleText";
import GamePageToolbar from "../../components/GamePageToolbar";
import {
  CollectionItemData,
  parseCollectionItems,
} from "../../utils/parseCollectionItems";
import {
  ColorblindImage,
  ColorblindImageContainer,
} from "../../components/ColorblindImage";
import { humanDisplayName } from "../../utils/displayName";
import { isNumberSenseQuestion } from "../../utils/RandomGenerators";

type PlayerMode = "account" | "guest";

interface TrialRecord {
  playerName: string;
  collectionId: number;
  collectionName: string;
  timeMs: number;
  itemCount: number;
  completedAt: string;
}

interface LeaderboardRow {
  playerName: string;
  timeMs: number;
}

const STORAGE_KEY = "timedTrialsRecords";

const shuffleItems = (items: CollectionItemData[]) => {
  const next = [...items];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
};

const getCollectionItems = (collection: Collection): CollectionItemData[] => {
  const fromDescription = parseCollectionItems(collection.description);
  if (fromDescription.length > 0) {
    return fromDescription;
  }

  return (collection.items ?? []).map((item) => ({
    name: item.name,
    svg: item.svg,
    count: item.count,
  }));
};

const loadRecords = (): TrialRecord[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TrialRecord[]) : [];
  } catch {
    return [];
  }
};

const saveRecord = (record: TrialRecord) => {
  const records = [...loadRecords(), record].slice(-200);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
};

const formatTime = (milliseconds: number) => {
  const totalSeconds = milliseconds / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = (totalSeconds % 60).toFixed(1);
  if (minutes === 0) {
    return `${seconds}s`;
  }
  return `${minutes}:${seconds.padStart(4, "0")}`;
};

const getLeaderboard = (collectionId: number): LeaderboardRow[] => {
  const bestByPlayer = new Map<string, LeaderboardRow>();

  loadRecords()
    .filter((record) => record.collectionId === collectionId)
    .forEach((record) => {
      const key = record.playerName.trim().toLowerCase();
      const currentBest = bestByPlayer.get(key);
      if (!currentBest || record.timeMs < currentBest.timeMs) {
        bestByPlayer.set(key, {
          playerName: record.playerName,
          timeMs: record.timeMs,
        });
      }
    });

  return Array.from(bestByPlayer.values()).sort(
    (left, right) => left.timeMs - right.timeMs,
  );
};

const TimedTrials: React.FC = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const hasSavedRef = useRef(false);

  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [accountName, setAccountName] = useState("Anonymous");
  const [playerMode, setPlayerMode] = useState<PlayerMode>("account");
  const [guestName, setGuestName] = useState("");
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(
    null,
  );
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isGameCompleted, setIsGameCompleted] = useState(false);
  const [sessionItems, setSessionItems] = useState<CollectionItemData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [activePlayerName, setActivePlayerName] = useState("");
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(0);

  useEffect(() => {
    const tourCompleted = localStorage.getItem("tourCompleted_timedTrials");
    if (!tourCompleted) {
      setIsTourRunning(true);
    }
  }, []);

  useEffect(() => {
    const loadSetup = async () => {
      try {
        const [fetchedCollections, profile] = await Promise.all([
          fetchCollections(getAccessTokenSilently),
          getCurrentUser(getAccessTokenSilently).catch(() => null),
        ]);
        setCollections(
          (fetchedCollections ?? []).filter(
            (collection: Collection) => collection.collection_id != null,
          ),
        );
        const profileName =
          profile && "display_name" in profile
            ? String(
                (profile as { display_name?: string }).display_name ?? "",
              )
            : "";
        setAccountName(
          humanDisplayName(profileName, user?.name, user?.email),
        );
      } catch (error) {
        console.error("Error loading timed trials setup:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSetup();
  }, [getAccessTokenSilently, user?.email, user?.name]);

  useEffect(() => {
    if (!isGameStarted || isGameCompleted || startedAt === null) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 50);

    return () => window.clearInterval(intervalId);
  }, [isGameStarted, isGameCompleted, startedAt]);

  const selectedCollection = useMemo(
    () =>
      collections.find(
        (collection) => collection.collection_id === selectedCollectionId,
      ) ?? null,
    [collections, selectedCollectionId],
  );

  const selectedItemCount = selectedCollection
    ? getCollectionItems(selectedCollection).length
    : 0;

  const resolvedPlayerName =
    playerMode === "account" ? accountName : guestName.trim();
  const canStart =
    selectedCollectionId !== null &&
    selectedItemCount > 0 &&
    resolvedPlayerName.length > 0;

  useEffect(() => {
    if (selectedCollectionId === null) {
      setLeaderboard([]);
      return;
    }
    setLeaderboard(getLeaderboard(selectedCollectionId));
  }, [selectedCollectionId, isGameCompleted]);

  const startGame = () => {
    if (!selectedCollection || !canStart) {
      return;
    }

    hasSavedRef.current = false;
    setActivePlayerName(resolvedPlayerName);
    setSessionItems(shuffleItems(getCollectionItems(selectedCollection)));
    setCurrentIndex(0);
    setElapsedMs(0);
    setStartedAt(Date.now());
    setIsGameCompleted(false);
    setIsGameStarted(true);
  };

  const finishTrial = (finalTimeMs: number) => {
    setElapsedMs(finalTimeMs);
    setIsGameCompleted(true);
    if (hasSavedRef.current || !selectedCollection) {
      return;
    }
    hasSavedRef.current = true;
    saveRecord({
      playerName: activePlayerName || resolvedPlayerName,
      collectionId: selectedCollection.collection_id,
      collectionName: selectedCollection.name,
      timeMs: finalTimeMs,
      itemCount: sessionItems.length,
      completedAt: new Date().toISOString(),
    });
    setLeaderboard(getLeaderboard(selectedCollection.collection_id));
  };

  const markGotIt = () => {
    if (!isGameStarted || isGameCompleted || startedAt === null) {
      return;
    }

    if (currentIndex + 1 >= sessionItems.length) {
      finishTrial(Date.now() - startedAt);
      return;
    }

    setCurrentIndex((index) => index + 1);
  };

  useEffect(() => {
    if (!isGameStarted || isGameCompleted) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }
      if (event.code === "Space" || event.key === "Enter") {
        event.preventDefault();
        markGotIt();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isGameStarted, isGameCompleted, currentIndex, sessionItems.length, startedAt]);

  const goBackToSetup = () => {
    setIsGameStarted(false);
    setIsGameCompleted(false);
    setSessionItems([]);
    setCurrentIndex(0);
    setStartedAt(null);
    setElapsedMs(0);
  };

  const startNextPlayer = () => {
    goBackToSetup();
    setPlayerMode("guest");
    setGuestName("");
  };

  const handleTourComplete = () => {
    setIsTourRunning(false);
    localStorage.setItem("tourCompleted_timedTrials", "true");
  };

  const handleBack = () => {
    navigate("/games");
  };

  const currentItem = sessionItems[currentIndex];

  const renderChallengeItem = (item: CollectionItemData) => {
    const prompt = isNumberSenseQuestion(item.name) ? (
      <p className="mb-3 text-center text-xl font-bold">{item.name}</p>
    ) : null;

    if (item.svg) {
      if (
        item.svg.startsWith("data:image") ||
        item.svg.startsWith("data:application")
      ) {
        return (
          <div className="flex flex-col items-center">
            {prompt}
            <ColorblindImage
              src={item.svg}
              alt={item.name}
              className="max-h-64 max-w-full object-contain"
            />
          </div>
        );
      }

      return (
        <div className="flex flex-col items-center">
          {prompt}
          <ColorblindImageContainer className="max-h-64 max-w-full">
            <div dangerouslySetInnerHTML={{ __html: item.svg }} />
          </ColorblindImageContainer>
        </div>
      );
    }

    if (item.name.startsWith("data:image")) {
      return (
        <ColorblindImage
          src={item.name}
          alt="Collection item"
          className="max-h-64 max-w-full object-contain"
        />
      );
    }

    return (
      <p className="max-w-[90vw] break-words text-center text-[clamp(2rem,8vmin,3.75rem)] font-bold">
        {item.name}
      </p>
    );
  };

  const renderLeaderboard = (highlightName?: string) => {
    if (leaderboard.length === 0) {
      return (
        <p className="text-sm">No times yet. Be the first on the board.</p>
      );
    }

    return (
      <ol className="w-full text-left">
        {leaderboard.map((row, index) => {
          const isCurrent =
            highlightName !== undefined &&
            row.playerName.trim().toLowerCase() ===
              highlightName.trim().toLowerCase();
          return (
            <li
              key={`${row.playerName}-${index}`}
              className={`mb-2 flex items-center justify-between rounded px-3 py-2 ${
                isCurrent ? "bg-yellow-200 text-black" : "bg-gray-100 text-black"
              }`}
            >
              <span>
                {index + 1}. {row.playerName}
              </span>
              <span className="font-semibold">{formatTime(row.timeMs)}</span>
            </li>
          );
        })}
      </ol>
    );
  };

  return (
    <div className={`page-container text-theme-text`}>
      <GamePageToolbar onBack={handleBack}>
        {isGameStarted && (
          <>
            {isGameCompleted && (
              <button
                type="button"
                onClick={startGame}
                className="reset-button max-w-[300px] cursor-pointer rounded border border-black bg-yellow-500 px-3 py-2 text-base font-bold uppercase text-black transition duration-300 hover:scale-105 hover:bg-yellow-600 active:scale-95 active:bg-yellow-700"
              >
                Try Again
              </button>
            )}
            <button
              type="button"
              onClick={isGameCompleted ? startNextPlayer : goBackToSetup}
              className="back-to-setup-button max-w-[300px] cursor-pointer rounded border border-black bg-blue-500 px-3 py-2 text-base font-bold uppercase text-white transition duration-300 hover:bg-blue-600"
            >
              {isGameCompleted ? "Next Player" : "Back to Setup"}
            </button>
          </>
        )}
      </GamePageToolbar>
      <h1 className="mb-8 text-3xl font-bold">
        <BubbleText>Timed Trials</BubbleText>
      </h1>

      {!isGameStarted ? (
        <div className="game-instructions flex w-full max-w-3xl flex-col items-center">
          <p className="inherit mb-6 text-center">
            Take turns and see who is faster. Use your account name, or type a
            player name so someone else can race on this device.
          </p>
          {isLoading ? (
            <p className="inherit">Loading collections...</p>
          ) : collections.length === 0 ? (
            <p className="inherit text-center">
              You need a collection to race. Create one first, then come back
              here.
            </p>
          ) : (
            <div className="collections-overview mb-6 w-full">
              <h2 className="inherit mb-3 text-xl font-semibold">
                Choose a collection
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {collections.map((collection) => {
                  const itemCount = getCollectionItems(collection).length;
                  const isSelected =
                    collection.collection_id === selectedCollectionId;

                  return (
                    <button
                      key={collection.collection_id}
                      type="button"
                      onClick={() =>
                        setSelectedCollectionId(collection.collection_id)
                      }
                      className={`rounded border-2 p-3 text-left transition duration-200 ${
                        isSelected
                          ? "border-blue-600 bg-blue-100 text-black"
                          : "border-black bg-white text-black hover:border-blue-400"
                      }`}
                    >
                      <p className="font-semibold">{collection.name}</p>
                      <p className="text-sm">
                        {itemCount} {itemCount === 1 ? "item" : "items"}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <fieldset className="player-name-options mb-4 w-full">
            <legend className="mb-2 font-semibold">Player name</legend>
            <label className="mb-2 flex items-center">
              <input
                type="radio"
                name="playerMode"
                checked={playerMode === "account"}
                onChange={() => setPlayerMode("account")}
                className="mr-2"
              />
              Use my account name ({accountName})
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="playerMode"
                checked={playerMode === "guest"}
                onChange={() => setPlayerMode("guest")}
                className="mr-2"
              />
              Enter a player name
            </label>
            {playerMode === "guest" && (
              <input
                type="text"
                value={guestName}
                onChange={(event) => setGuestName(event.target.value)}
                placeholder="Alex, Sam, Jordan..."
                className="mt-3 w-full rounded border border-black bg-white p-2 text-black"
              />
            )}
          </fieldset>

          {selectedCollectionId !== null && (
            <div className="mb-6 w-full">
              <h2 className="inherit mb-3 text-xl font-semibold">
                Fastest times
              </h2>
              {renderLeaderboard()}
            </div>
          )}

          <button
            type="button"
            onClick={startGame}
            disabled={!canStart}
            className={`start-button max-w-[300px] rounded border border-black bg-green-500 p-2.5 text-base font-bold uppercase text-white transition duration-300 hover:bg-green-600 ${
              canStart ? "cursor-pointer" : "cursor-not-allowed opacity-50"
            }`}
          >
            Start Trial
          </button>
        </div>
      ) : (
        <div className="timed-trials-game flex w-full max-w-3xl flex-col items-center">
          <p className="mb-2 text-lg font-semibold">{activePlayerName}</p>
          <div className="mb-6 grid w-full grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-sm uppercase">Time</p>
              <p className="text-3xl font-bold">{formatTime(elapsedMs)}</p>
            </div>
            <div>
              <p className="text-sm uppercase">Progress</p>
              <p className="text-3xl font-bold">
                {isGameCompleted ? sessionItems.length : currentIndex + 1}/
                {sessionItems.length}
              </p>
            </div>
          </div>

          {isGameCompleted ? (
            <div className="w-full text-center">
              <p className="mb-4 text-2xl font-semibold">
                {activePlayerName} finished in {formatTime(elapsedMs)}.
              </p>
              <h2 className="inherit mb-3 text-xl font-semibold">
                Fastest times
              </h2>
              {renderLeaderboard(activePlayerName)}
            </div>
          ) : (
            currentItem && (
              <>
                <div className="mb-8 flex min-h-[min(280px,40vh)] w-full items-center justify-center rounded-lg border border-black bg-white p-6 text-black">
                  {renderChallengeItem(currentItem)}
                </div>
                <button
                  type="button"
                  onClick={markGotIt}
                  className="rounded border border-black bg-green-500 px-6 py-3 text-lg font-bold uppercase text-white hover:bg-green-600"
                >
                  Got it
                </button>
                <p className="inherit mt-4 text-sm">
                  Item {currentIndex + 1} of {sessionItems.length}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  Space or Enter = Got it.
                </p>
              </>
            )
          )}
        </div>
      )}

      <GuidedTour
        steps={tourStepsTimedTrials()}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={setCurrentTourStep}
        tourName="timedTrials"
      />
    </div>
  );
};

export default TimedTrials;
